import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "flowbite-react";
import { useNavigate, useLocation } from "react-router-dom";
import { NavBar, SideNav, Breadcrumb } from "../../components";
import PostEditor from "./PostEditor";
import IPhonePreview from "./iPhonePreview";
import AiRestructureModal from "./AiRestructureModal";
import StructuredFieldsPanel from "./StructuredFieldsPanel";
import {
  createPost,
  fetchPostsById,
  publishPost,
  setSelectedPost,
  updatePost,
} from "../../redux/slices/posts";
import { AiOutlineLoading } from "react-icons/ai";
import { toast } from "react-toastify";
import { restructureContent } from "../../config/openai";
import api from "../../config/axios";
import { wrapPostPreviewHtml } from "../../helpers/postBodyHtml";
import { sanitizeHtmlForQuill } from "../../helpers/sanitizeQuillHtml";
import {
  emptyScholarshipFields,
  emptySchoolFields,
  mergeExtractedIntoScholarship,
  mergeExtractedIntoSchool,
  scholarshipFieldsFromPost,
  scholarshipPayloadFromFields,
  schoolFieldsFromPost,
  schoolPayloadFromFields,
} from "../../helpers/advisorVocab";

const SOURCE_LINK_CATEGORIES = ["article", "school", "scholarship"];
const STRUCTURED_CATEGORIES = ["school", "scholarship"];

// TODO(ai-advisor): Prompt (or require) auto-extract / manual structured fields
// before save/publish when a school or scholarship still has empty facets.
// Apps keep rendering the HTML `body`; the advisor matches on typed fields
// (country, deadlineAt, levels, …). Dashboard-created records that only have
// body are weak Match candidates until extracted. Bulk backfill of existing
// live docs belongs on the Completeness page.

function formatRestructureError(err) {
  if (err == null) {
    return "Could not restructure content.";
  }
  if (typeof err === "string") {
    return err;
  }
  if (typeof err === "object") {
    const msg = err.message || err.error?.message;
    if (typeof msg === "string" && msg.trim()) {
      return msg;
    }
  }
  return "Could not restructure content.";
}

/**
 * Draft vs published drives which API (`drafts` vs `articles`) and buttons.
 * Prefer `isDraft` from the server (GET /posts/:id sets it from the collection used).
 * If missing, fall back to ?isDraft=true only when the doc is not explicitly published.
 */
function isEditingDraftPost(loadedPost, urlSaysDraft) {
  if (!loadedPost) return false;
  if (loadedPost.isDraft === false) return false;
  if (loadedPost.isDraft === true) return true;
  return urlSaysDraft === true;
}

const CreatePost = ({ parentPath, category }) => {
  const location = useLocation();
  // Get post ID from url
  const urlPath = location.pathname.replace(/\/$/, "");
  const id = urlPath.split("/").pop();
  const { selectedPost } = useSelector((state) => state.posts);
  const urlSaysDraft =
    new URLSearchParams(location.search).get("isDraft") === "true";

  let populateData;
  if (selectedPost && String(selectedPost.id) === String(id)) {
    populateData = selectedPost;
  }

  const loadedPost = populateData;
  const editingDraft = isEditingDraftPost(loadedPost, urlSaysDraft);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { actionLoading, pageLoading } = useSelector((state) => state.posts);
  const [bodyContent, setBodyContent] = useState(
    (populateData && populateData.body) || ""
  );
  const [titleContent, setTitleContent] = useState(
    (populateData && populateData.title) || ""
  );
  const [tags, setTags] = useState(populateData && populateData.tags);
  const [thumbnail, setThumbnail] = useState(
    populateData && populateData.thumbnail
  );
  const [featured, setFeatured] = useState(
    (populateData && populateData.featured) || false
  );
  const [isNewPost, setIsNewPost] = useState(id === "clone" || id === "new");
  const [actionType, setActionType] = useState(null);
  const [aiRestructureLoading, setAiRestructureLoading] = useState(false);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [editorSyncCount, setEditorSyncCount] = useState(0);
  const [structuredFields, setStructuredFields] = useState(() =>
    category === "scholarship" ? emptyScholarshipFields() : emptySchoolFields()
  );
  const [extractLoading, setExtractLoading] = useState(false);
  const [completenessScore, setCompletenessScore] = useState(null);

  const applyLoadedStructured = useCallback((post) => {
    if (!STRUCTURED_CATEGORIES.includes(category) || !post) return;
    if (category === "school") {
      setStructuredFields(schoolFieldsFromPost(post));
    } else {
      setStructuredFields(scholarshipFieldsFromPost(post));
    }
    setCompletenessScore(
      typeof post.completenessScore === "number" ? post.completenessScore : null
    );
  }, [category]);

  useEffect(() => {
    if (id === "new") {
      setStructuredFields(
        category === "scholarship"
          ? emptyScholarshipFields()
          : emptySchoolFields()
      );
      setCompletenessScore(null);
    }
  }, [id, category]);

  useEffect(() => {
    if (populateData && STRUCTURED_CATEGORIES.includes(category)) {
      applyLoadedStructured(populateData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [populateData?.id]);

  useEffect(() => {
    const didLoad = async () => {
      if (id === "new" || id === "clone") return;
      const params = new URLSearchParams(location.search);
      const isDraftParam = params.get("isDraft");
      try {
        const resultAction = await dispatch(
          fetchPostsById({ isDraft: isDraftParam, id })
        );
        if (fetchPostsById.fulfilled.match(resultAction)) {
          const post = resultAction.payload;
          setTitleContent(post.title);
          setBodyContent(post.body);
          setTags(post.tags);
          setThumbnail(post.thumbnail);
          setFeatured(post.featured);
          applyLoadedStructured(post);
          setEditorSyncCount((c) => c + 1);

          // Sync URL when the doc lives in the other collection (e.g. review
          // links to a draft school without ?isDraft=true).
          const actualDraft = post.isDraft === true;
          const urlSaysDraft = isDraftParam === "true";
          if (actualDraft !== urlSaysDraft) {
            const next = new URLSearchParams(location.search);
            next.set("isDraft", actualDraft ? "true" : "false");
            navigate(
              { pathname: location.pathname, search: `?${next.toString()}` },
              { replace: true }
            );
          }
        }
      } catch {}
    };

    void didLoad();
  }, [id, location.search, location.pathname, navigate, dispatch, applyLoadedStructured]);

  const structuredPayload = () => {
    if (!STRUCTURED_CATEGORIES.includes(category)) return {};
    if (category === "school") {
      return schoolPayloadFromFields(structuredFields);
    }
    return scholarshipPayloadFromFields(structuredFields);
  };

  const handleAutoExtract = async () => {
    if (!STRUCTURED_CATEGORIES.includes(category)) return;
    if (!bodyContent || !String(bodyContent).trim()) {
      toast.error("Add body content before extracting.");
      return;
    }
    try {
      setExtractLoading(true);
      const response = await api.post("/admin/ai/extract-fields", {
        category,
        body: bodyContent,
      });
      const extracted = response.data.payload || {};
      if (category === "school") {
        setStructuredFields((prev) =>
          mergeExtractedIntoSchool(prev, extracted)
        );
      } else {
        setStructuredFields((prev) =>
          mergeExtractedIntoScholarship(prev, extracted)
        );
      }
      if (typeof extracted.completenessScore === "number") {
        setCompletenessScore(extracted.completenessScore);
      }
      toast.success("Structured fields extracted — review before saving.");
    } catch (err) {
      const msg =
        err.response?.data?.errors?.global ||
        err.message ||
        "Extraction failed";
      toast.error(msg);
    } finally {
      setExtractLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const type = e.nativeEvent.submitter.name;
    setActionType(type);
    const submitAsDraft = type === "draft";

    if (id === "clone") {
      create(submitAsDraft, null, true);
      return;
    }

    if (id === "new") {
      create(submitAsDraft);
      return;
    }

    // Loaded from `articles` — never use draft save/publish paths (avoids update(true) → drafts/{id}
    // when ?isDraft=true or stale state wrongly implied a draft).
    if (loadedPost?.isDraft === false) {
      if (submitAsDraft) {
        return;
      }
      update(false);
      return;
    }

    if (editingDraft && submitAsDraft) {
      update(true);
      return;
    }

    if (editingDraft && !submitAsDraft) {
      // Publish reads drafts/{id} on the server; unsaved editor state (e.g. AI-restructured body)
      // must be persisted with update(true) first or the old crawled body is what gets published.
      const saved = await update(true);
      if (saved) {
        await publishDraftPost();
      }
      return;
    }

    if (loadedPost && !editingDraft) {
      update(submitAsDraft);
      return;
    }

    create(submitAsDraft);
  };

  const update = async (isDraft) => {
    try {
      let data = {
        title: titleContent,
        body: bodyContent,
        tags:
          Array.isArray(tags) === true
            ? tags
            : tags.split(",").map((item) => item.trim()),
        isDraft,
        category,
        featured: featured || false,
        ...structuredPayload(),
      };

      const priorSource =
        populateData?.sourceUrl ||
        (selectedPost?.id === id ? selectedPost?.sourceUrl : undefined);
      if (
        SOURCE_LINK_CATEGORIES.includes(category) &&
        priorSource &&
        String(priorSource).trim() !== ""
      ) {
        data.sourceUrl = priorSource;
      }

      if (thumbnail) {
        data.thumbnail = thumbnail;
      }

      const resultAction = await dispatch(
        updatePost({
          id,
          data,
          oldThumbnail: populateData && populateData.thumbnail,
        })
      );
      if (updatePost.fulfilled.match(resultAction)) {
        if (isDraft === true) {
          dispatch(setSelectedPost(resultAction.payload));
        } else {
          navigate(parentPath);
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const publishDraftPost = async () => {
    try {
      const resultAction = await dispatch(publishPost({ id }));
      if (publishPost.fulfilled.match(resultAction)) {
        navigate(parentPath);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const create = async (isDraft, id, isClone = false) => {
    try {
      let data = {
        title: titleContent,
        body: bodyContent,
        tags:
          Array.isArray(tags) === true
            ? tags
            : tags.split(",").map((item) => item.trim()),
        isDraft,
        category,
        thumbnail,
        featured: featured || false,
        ...structuredPayload(),
      };
      if (id) {
        data.draftId = id;
      }
      const resultAction = await dispatch(createPost(data));
      setIsNewPost(false);
      if (createPost.fulfilled.match(resultAction)) {
        if (isClone === true && isDraft === true) {
          navigate(`${parentPath}/${resultAction.payload.id}?isDraft=true`);
        } else if (isDraft === true) {
          dispatch(setSelectedPost(resultAction.payload));
          navigate(`${parentPath}/${resultAction.payload.id}?isDraft=true`);
        } else {
          navigate(parentPath);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleAiRestructureConfirm = async ({ extraUserInstructions }) => {
    try {
      setAiRestructureLoading(true);
      const innerHtml = await restructureContent(bodyContent, {
        extraUserInstructions: extraUserInstructions || undefined,
      });
      const safe = sanitizeHtmlForQuill(innerHtml);
      const wrapped = wrapPostPreviewHtml(safe);
      setBodyContent(wrapped);
      setEditorSyncCount((c) => c + 1);
      setAiModalOpen(false);
      toast.success("Content restructured.");
    } catch (err) {
      const message = formatRestructureError(err);
      toast.error(message);
      console.error(err);
    } finally {
      setAiRestructureLoading(false);
    }
  };

  const sourceUrlForDisplay =
    id !== "new" && id !== "clone"
      ? populateData?.sourceUrl ??
        (selectedPost?.id === id ? selectedPost?.sourceUrl : undefined)
      : undefined;

  return (
    <div className="antialiased bg-gray-50 dark:bg-gray-900">
      <NavBar />
      <SideNav />
      <main className="p-4 md:ml-64 h-auto pt-20">
        <div className="sticky top-14 z-10 bg-gray-50 dark:bg-gray-900">
          <Breadcrumb />
        </div>
        {pageLoading ? ( // Skeleton Loader
          <div className="mt-2 flex animate-pulse">
            <div className="flex flex-col w-full max-w-[800px] gap-6 bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
              <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded w-full" />
              <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-full" />
              <div className="h-40 bg-gray-200 dark:bg-gray-700 rounded w-full" />
              <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-full" />
              <div className="flex gap-4">
                <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-24" />
                <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-24" />
              </div>
            </div>
            <div className="ml-4 h-[500px] w-[300px] bg-gray-200 dark:bg-gray-700 rounded" />
          </div>
        ) : (
          <div className="mt-2 flex">
            <form
              className="flex flex-col w-full max-w-[800px] gap-6 bg-white dark:bg-gray-800 p-4 rounded-lg shadow"
              onSubmit={handleSubmit}
            >
              <div className="">
                <input
                  type="text"
                  value={titleContent}
                  className="font-display block py-2.5 px-0 w-full text-4xl text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-purple-500 focus:outline-none focus:ring-0 focus:border-purple-600 peer"
                  placeholder="Enter Title"
                  onChange={(e) => setTitleContent(e.target.value)}
                  required
                />
              </div>
              {SOURCE_LINK_CATEGORIES.includes(category) &&
                sourceUrlForDisplay &&
                String(sourceUrlForDisplay).trim() !== "" && (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-left text-sm dark:border-gray-600 dark:bg-gray-700/50">
                    <span className="font-medium text-gray-700 dark:text-gray-200">
                      Original source
                    </span>
                    <div className="mt-1 truncate">
                      <a
                        href={sourceUrlForDisplay}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-purple-600 hover:underline dark:text-purple-400"
                      >
                        {sourceUrlForDisplay}
                      </a>
                    </div>
                  </div>
                )}
              <div>
                <label
                  className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                  for="file_input"
                >
                  Upload Thumbnail
                </label>
                <input
                  className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 dark:text-gray-400 focus:outline-none dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400"
                  aria-describedby="file_input_help"
                  id="file_input"
                  type="file"
                  accept="image/*"
                  onChange={(e) => setThumbnail(e.target.files[0])}
                  required={!thumbnail}
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                  Body
                </label>
                <PostEditor
                  value={populateData && populateData.body}
                  editorSyncCount={editorSyncCount}
                  syncFromStoredBody={bodyContent}
                  showAiFab
                  onAiFabClick={() => setAiModalOpen(true)}
                  aiFabDisabled={aiRestructureLoading || actionLoading}
                  updateBodyContent={(content) => {
                    setBodyContent(content);
                  }}
                />
                <AiRestructureModal
                  show={aiModalOpen}
                  onClose={() => !aiRestructureLoading && setAiModalOpen(false)}
                  onConfirm={handleAiRestructureConfirm}
                  isLoading={aiRestructureLoading}
                />
              </div>
              {STRUCTURED_CATEGORIES.includes(category) && (
                <StructuredFieldsPanel
                  category={category}
                  value={structuredFields}
                  onChange={setStructuredFields}
                  onExtract={handleAutoExtract}
                  extractLoading={extractLoading}
                  extractDisabled={
                    actionLoading || aiRestructureLoading || !bodyContent
                  }
                  completenessScore={completenessScore}
                />
              )}
              <input
                type="text"
                id="tags"
                value={tags}
                class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-purple-500 focus:border-purple-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-purple-500 dark:focus:border-purple-500"
                placeholder="Enter tags seperated by comma (,)"
                onChange={(e) => setTags(e.target.value)}
                required
              />
              <div>
                <label class="inline-flex items-center cursor-pointer">
                  <span class="ms-3 text-sm font-medium text-gray-900 dark:text-gray-300 mr-4">
                    Mark as featured
                  </span>
                  <input
                    type="checkbox"
                    value=""
                    class="sr-only peer"
                    checked={featured}
                    onChange={(e) => setFeatured(e.target.checked)}
                  />
                  <div class="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 dark:peer-focus:ring-purple-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-purple-600 dark:peer-checked:bg-purple-600"></div>
                </label>
                <div />
              </div>
              <div className="flex gap-4">
                {loadedPost &&
                !editingDraft &&
                id !== "clone" ? (
                  <></>
                ) : (
                  <Button
                    color="purple"
                    size="sm"
                    name="draft"
                    type="submit"
                    disabled={actionLoading}
                    isProcessing={actionLoading && actionType === "draft"}
                    processingSpinner={
                      <AiOutlineLoading className="h-6 w-6 animate-spin" />
                    }
                  >
                    Save {isNewPost ? "as" : ""} Draft
                  </Button>
                )}
                <Button
                  color="red"
                  size="sm"
                  name="publish"
                  type="submit"
                  disabled={actionLoading}
                  isProcessing={actionLoading && actionType === "publish"}
                  processingSpinner={
                    <AiOutlineLoading className="h-6 w-6 animate-spin" />
                  }
                >
                  Publish{" "}
                  {loadedPost &&
                    !editingDraft &&
                    id !== "clone" &&
                    "Update"}
                </Button>
              </div>
            </form>
            <IPhonePreview
              thumbnail={thumbnail}
              title={`<span style="font-family: 'Playfair Display', serif; font-size: 25px;"}>${titleContent}</span>`}
              body={bodyContent}
            />
          </div>
        )}
      </main>
    </div>
  );
};

export default CreatePost;
